import { computed, inject, Injectable, signal } from '@angular/core';
import { AuthService, injectUser } from '@quipex/core/auth';
import { APP_CONFIG_TOKEN, createContactUsHref } from '@quipex/core/config';
import {
    injectViewportBreakpoints,
    SidebarPortalService,
} from '@quipex/core/utils';
import { PermissionKey } from '@quipex/shared/contracts';
import {
    hasAccessToNavItem,
    type DeviceTarget,
    type NavItem,
} from '@quipex/shared/ui';
import { Subject } from 'rxjs';

@Injectable()
export class UiState {
    //#region Dependency injection
    private readonly me = injectUser();
    private readonly viewport = injectViewportBreakpoints();
    private readonly appConfig = inject(APP_CONFIG_TOKEN);
    private readonly sidebarPortalService = inject(SidebarPortalService);
    private readonly authService = inject(AuthService);
    //#endregion

    //#region State
    public readonly hovered = signal(false);

    public readonly isRailOrWider = this.viewport.atLeast('tablet768');

    public readonly isOffCanvas = this.viewport.lessThan('tablet768');

    public readonly isProd = this.appConfig.isProd;
    public readonly isDemo = this.appConfig.isDemo;
    public readonly portalContent = this.sidebarPortalService.content;
    public readonly hasPortalContent = this.sidebarPortalService.hasContent;
    public readonly contactUsHref = createContactUsHref(
        this.appConfig.supportEmail
    );
    public readonly inAppHelpSupportAvailable = this.contactUsHref !== null;
    private readonly findBuildingRequestSource = new Subject<void>();
    public readonly findBuildingRequested$ =
        this.findBuildingRequestSource.asObservable();

    // --- Base menus (definitions) ---
    private readonly _navItemsGeneral = computed<NavItem[]>(() => [
        {
            kind: 'link',
            key: 'home',
            label: 'Home',
            route: '/',
            icon: 'lucideHouse',
            exact: true,
        },
        {
            kind: 'button',
            key: 'find-building',
            label: 'Find building',
            icon: 'lucideSearch',
            onClick: () => this.findBuildingRequestSource.next(),
        },
        {
            kind: 'link',
            key: 'buildings',
            label: 'My buildings',
            route: '/buildings',
            icon: 'lucideBuilding2',
        },
    ]);

    private readonly _navItemsOrg = computed<NavItem[]>(() => [
        {
            kind: 'link',
            key: 'users',
            label: 'Users',
            route: '/organisation/users',
            icon: 'lucideUsers',
            orgPermissions: [
                PermissionKey.OrgSettingsRead,
                PermissionKey.OrgUsersRead,
                PermissionKey.OrgUsersRoleUpdate,
            ],
        },
        {
            kind: 'link',
            key: 'settings',
            label: 'Settings',
            route: '/organisation/settings',
            icon: 'lucideSettings',
            orgPermissions: [PermissionKey.OrgSettingsUpdate],
        },
        {
            kind: 'link',
            key: 'billing',
            label: 'Billing',
            route: '/organisation/billing',
            icon: 'lucideCreditCard',
            orgPermissions: [PermissionKey.OrgBillingRead],
        },
        {
            kind: 'link',
            key: 'pack-templates',
            label: 'Pack templates',
            route: '/organisation/pack-templates',
            icon: 'lucideNotebookTabs',
            orgPermissions: [
                PermissionKey.OrgDocumentPackTemplatesRead,
                PermissionKey.OrgDocumentPackTemplatesUpdate,
            ],
        },
    ]);

    public readonly resolvedNavItemsGeneral = computed<NavItem[]>(() =>
        this.filterNavItems(this._navItemsGeneral())
    );

    public readonly resolvedNavItemsOrg = computed<NavItem[]>(() =>
        this.filterNavItems(this._navItemsOrg())
    );
    //#endregion

    //#region Public methods
    public isEffectiveExpanded(isExpanded: boolean): boolean {
        return this.isRailOrWider() ? isExpanded || this.hovered() : isExpanded;
    }

    public onMouseEnter(): void {
        if (this.isOffCanvas()) return;

        this.hovered.set(true);
    }

    public onMouseLeave(): void {
        if (this.isOffCanvas()) return;

        this.hovered.set(false);
    }

    public signout(): void {
        this.authService.logout();
    }
    //#endregion

    //#region Private methods
    /** Builds a device predicate for current viewport */
    private createDevicePredicate(isOffCanvas: boolean) {
        return (device: DeviceTarget = 'both'): boolean => {
            if (device === 'both') return true;

            const comparedDevice = isOffCanvas ? 'mobile' : 'desktop';
            return device === comparedDevice;
        };
    }

    private createAccessFilter(isOffCanvas: boolean) {
        const allowDevice = this.createDevicePredicate(isOffCanvas);
        return (item: NavItem): boolean => {
            if (!allowDevice(item.device)) return false;
            return hasAccessToNavItem(item, { user: this.me() });
        };
    }

    private filterNavItems(items: NavItem[]): NavItem[] {
        const isOffCanvas = this.isOffCanvas();
        const filterByAccess = this.createAccessFilter(isOffCanvas);
        return items.filter(filterByAccess);
    }
    //#endregion
}
